var _link;
/* global jwplayer */
import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useFeatures } from '@atc/features';
import { injectScript } from '@atc/dom-fns';

/**
 * The JWplayer leverages a 3rd party library injected in to the DOM. This component creates an instance of the player along with
 * controls and callbacks. All properties of the video instance are stored in 'this.player'.
 * It will also create a separate iframe for unity works reporting
 */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function JWPlayer(_ref) {
  let {
    autostart = false,
    base,
    config,
    className,
    controlbar = 'bottom',
    dock = false,
    height,
    image,
    mute,
    onPlay,
    onPlayerReady,
    onPause,
    onReplay,
    onCompleted,
    onMilestones,
    provider,
    playlist,
    repeat,
    smoothing = false,
    stretching = 'uniform',
    src,
    videoId,
    width,
    tracks,
    placeholder
  } = _ref;
  const {
    use_reaxl_jwplayer: [enableReaxlJwplayer]
  } = useFeatures(['use_reaxl_jwplayer']);
  const playerRef = useRef(null);
  const playerData = useRef({
    isReplay: false,
    isSeek: false,
    isPlaylist: false,
    hasPlayed: false,
    hasReachedQuarter: false,
    hasReachedHalf: false,
    hasReachedThreeQuarters: false,
    hasFinished: false,
    player: null,
    loadedPlaylist: null,
    repFrameElm: null,
    statePlaying: null,
    stateBuffering: null,
    stateIdle: null,
    statePaused: null,
    stateComplete: null,
    unityTrackPlayed: null
  });
  const [isVideoReady, setIsVideoReady] = useState(false);

  /**
  * Validate a callback function then execute the valid callback.
  * @param {Function} cb callback function to be executed
  * @param {Iterable} params paramters required by the callback funciton
  * @private
  * @return undefined
  */
  const executeCallback = function (cb, params) {
    if (params === void 0) {
      params = [];
    }
    if (typeof cb === 'function') cb(...params);
  };

  /**
  * Event handler for onTime event. Used to emit BIRF events on duration quartiles.
  * @param event
  */
  const onTime = event => {
    // Duration and position are in seconds
    const {
      duration,
      position
    } = event;
    const roundedDuration = Math.round(duration);
    const roundedPosition = Math.round(position);

    // Calculate quartiles
    const quarter = Math.round(duration / 4);
    const half = Math.round(duration / 2);
    const threeQuarters = Math.round(duration * 3 / 4);

    // Fire BIRF event for each quartile
    if (roundedPosition === quarter && !playerData.current.hasReachedQuarter) {
      playerData.current.hasReachedQuarter = true;
      executeCallback(onMilestones, ['25%']);
    } else if (roundedPosition === half && !playerData.current.hasReachedHalf) {
      playerData.current.hasReachedHalf = true;
      executeCallback(onMilestones, ['50%']);
    } else if (roundedPosition === threeQuarters && !playerData.current.hasReachedThreeQuarters) {
      playerData.current.hasReachedThreeQuarters = true;
      executeCallback(onMilestones, ['75%']);
    } else if (roundedPosition === roundedDuration - 2 && !playerData.current.hasFinished) {
      playerData.current.hasFinished = true;
      executeCallback(onMilestones, ['100%']);
    }
  };

  /**
  * Creates an IFRAME and append it to the DOM and use/reused for unityWorks reporting
  * @returns {HTMLElement}
  * @private
  */
  const createReportIframe = () => {
    const repIFrameId = 'jwReportingIframe';
    let repIFrame = document.getElementById(repIFrameId);
    if (!repIFrame) {
      const bodyElm = document.getElementsByTagName('body');
      const divElm = document.createElement('div');
      repIFrame = document.createElement('iframe');
      repIFrame.setAttribute('id', repIFrameId);
      repIFrame.setAttribute('width', 0);
      repIFrame.setAttribute('height', 0);
      repIFrame.setAttribute('src', 'about:blank');
      divElm.setAttribute('id', repIFrameId + '-div');
      divElm.style.display = 'none';
      divElm.appendChild(repIFrame);
      bodyElm.appendChild(divElm);
    }
    return repIFrame;
  };

  /**
  * Source unityWorks reporting url within an iframe
  * @param srcURI {String} url extracted from the playlist
  * @private
  */
  const reportUnityWorks = srcURI => {
    try {
      if (!playerData.current.repFrameElm) {
        playerData.current.repFrameElm = createReportIframe();
      }
      playerData.current.repFrameElm.attr('src', srcURI);
    } catch (err) {
      console.error('could not create unity works report frame', err); // eslint-disable-line no-console
    }
  };

  /**
  * Playlist metadata attribute 'uwmViewLog' reported back to UnityWorks via iframe
  * @param event {Object} jwplayer event object
  * @return undefined
  * @private
  */
  const onUnityWorksHandler = event => {
    try {
      const track = playerData.current.player ? playerData.current.player.getPlaylistItem(playerData.current.player.getPlaylistIndex()) : null;
      let unityURL = null;
      if (track && !playerData.current.unityTrackPlayed) {
        if (typeof track.mediaid !== 'undefined') {
          unityURL = track.mediaid;
          reportUnityWorks(unityURL);
          playerData.current.unityTrackPlayed = true;
        }
      }
    } catch (err) {
      console.error('could not report unity works report', err); // eslint-disable-line no-console
    }
  };

  /**
  * Setup onPlay after new playList load for UnityWorks reporting
  * @param event {Object} jwplayer event object
  * @return undefined
  */
  const preUnityWorksHandler = event => {
    playerData.current.unityTrackPlaye = false;
    playerData.current.player.on('play', onUnityWorksHandler);
  };

  /**
  * Cache a local reference to the loaded playlist
  * @param event {Object} jwplayer event object
  * @return undefined
  */
  const onPlaylistLoaded = event => {
    playerData.current.loadedPlaylist = event.playlist;
  };

  /**
  * Keeps track of where or not the user seeked/scrubbed the player
  * @param event {Object} jwplayer event object
  * @return undefined
  */
  const onPlayerSeek = event => {
    playerData.current.isSeek = true;
  };

  /**
  * Event handler fired when playlist is completed
  * @param event {Object} jwplayer event object
  * @return undefined
  */
  const onPlaylistComplete = event => {
    playerData.current.hasPlayed = true;
  };
  const resetTimeMilestones = () => {
    playerData.current.hasReachedQuarter = false;
    playerData.current.hasReachedHalf = false;
    playerData.current.hasReachedThreeQuarters = false;
    playerData.current.hasFinished = false;
  };

  /**
  * Event handler fired when play is completed
  * @param event {Object} jwplayer event object
  * @return undefined
  */
  const onPlayerComplete = event => {
    resetTimeMilestones();
    playerData.current.hasPlayed = !playerData.current.isPlaylist;
    onCompleted();
  };

  /**
  * Gets JW Player Playlist state
  * @return {Object}.
  * @private
  */
  const getJwPlaylistState = () => {
    const listParams = {
      playlistLength: 1,
      playlistIndex: 0
    };
    try {
      listParams.playlistLength = playerData.current.player.getPlaylist().length;
      listParams.playlistIndex = playerData.current.player.getPlaylistIndex(); // returns the playlist index
    } catch (err) {
      console.error('could not get playlist', err); // eslint-disable-line no-console
    }
    return listParams;
  };

  /**
  * Event handler fired when playback state changes
  * @param event {Object} jwplayer event object
  * @return undefined
  */
  const onPlayerStateHandler = event => {
    const mediaState = getJwPlaylistState();
    if (mediaState.playlistIndex !== 0) {
      return;
    }
    if (event.oldstate === 'playing' && event.newstate === 'paused') {
      executeCallback(onPause, []);
      playerData.current.statePaused = true;
      playerData.current.statePlaying = false;
    } else if (playerData.current.hasPlayed && event.oldstate !== 'paused') {
      executeCallback(onReplay, []);
      playerData.current.isReplay = true;
      playerData.current.statePaused = false;
      playerData.current.statePlaying = true;
    } else if (event.oldstate === 'buffering' && event.newstate === 'playing') {
      executeCallback(onPlay, []);
      playerData.current.statePaused = false;
      playerData.current.statePlaying = true;
    }
  };

  /**
  * Event handler fired when player is loaded and ready.
  * @private
  */
  const playerOnReady = () => {
    /* Set Listeners */
    setIsVideoReady(true);
    playerData.current.player.on('play', onPlayerStateHandler);
    playerData.current.player.on('pause', onPlayerStateHandler);
    playerData.current.player.on('complete', onPlayerComplete);
    if (playerData.current.isPlaylist) {
      playerData.current.player.on('playlistComplete', onPlaylistComplete);
    }
    playerData.current.player.on('seek', onPlayerSeek);
    playerData.current.player.on('playlist', onPlaylistLoaded);
    playerData.current.player.on('playlistItem', preUnityWorksHandler);
    playerData.current.player.on('time', onTime);
    onPlayerReady();
  };

  /**
   * Catch Video Setup Error
   */
  const playerSetupError = () => {
    setIsVideoReady(true);
  };

  /**
  * Load JWPlayer
  */

  const load = () => {
    try {
      jwplayer.key = 'RFxo/Y6bAi5bVh/hAJ6Dm2vC9vFZ7Q0+GsO7Sp2IgS/mF6db';
      playerData.current.statePlaying = jwplayer.events.state.PLAYING;
      playerData.current.stateBuffering = jwplayer.events.state.BUFFERING;
      playerData.current.stateIdle = jwplayer.events.state.IDLE;
      playerData.current.statePaused = jwplayer.events.state.PAUSED;
      playerData.current.stateComplete = jwplayer.events.state.COMPLETE;
      const params = {
        width,
        height,
        image,
        provider,
        autostart,
        repeat,
        mute,
        dock,
        smoothing,
        stretching,
        controlbar,
        base,
        skin: {
          name: 'atc'
        },
        logo: {
          hide: true
        },
        analytics: {
          enabled: false,
          cookies: false
        }
      };
      const checkRSS = /\/rss/i;
      if (src.match(checkRSS) !== null) {
        Object.assign(params, {
          playlist: src
        });
      } else if (playlist) {
        playerData.current.isPlaylist = true;
        Object.assign(params, {
          playlist
        });
      } else {
        const source = {
          file: src
        };
        if (tracks) {
          source.tracks = tracks;
        }
        Object.assign(params, source);
      }
      if (src) {
        playerData.current.player = jwplayer(playerRef.current.id).setup(params);
        playerData.current.player.on('ready', playerOnReady);
        playerData.current.player.on('setupError', playerSetupError);
      }
    } catch (err) {
      console.error('could not load jwPlayer', err); // eslint-disable-line no-console
    }
  };
  useEffect(() => {
    if (typeof jwplayer === 'undefined') {
      if (enableReaxlJwplayer) {
        import('../../assets/jwplayer/jwplayer').then(() => {
          load();
        });
      } else {
        injectScript(config.RESOURCE_URL.JWPLAYER_JS).then(() => {
          load();
        });
      }
    } else {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => () => {
    if (playerData.current.player) {
      playerData.current.player.remove();
    }
  }, []);
  return /*#__PURE__*/_jsxs("div", {
    "data-cmp": "jwPlayer",
    className: `${className || ''} video-player`,
    children: [enableReaxlJwplayer && (_link || (_link = /*#__PURE__*/_jsx("link", {
      rel: "stylesheet",
      type: "text/css",
      href: "/content/vendor/jwplayer/atc.css"
    }))), /*#__PURE__*/_jsx("div", {
      id: `v${videoId}`,
      ref: playerRef,
      children: !isVideoReady && placeholder
    })]
  });
}
export default JWPlayer;